import { css, FlattenSimpleInterpolation } from 'styled-components';
import curry from 'lodash/curry';
import isEmpty from 'lodash/isEmpty';
import isObject from 'lodash/isObject';
import isString from 'lodash/isString';
import mapValues from 'lodash/mapValues';
import merge from 'lodash/merge';
import pickBy from 'lodash/pickBy';

import IThemeInterface from '../../Theme/common';
import { MakeMediaMap } from '../Typing/tsHelpers';

import { makeMediaObj } from './makeMediaObj';
import { calcMediaStyles } from './CalcMediaStyles/calcMediaStyles';
import { normalizeMediaStyles } from './normalizeMediaStyles';

export type VALID_EDGE_STYLE_MEDIA_MAP = MakeMediaMap<string | IEdgeStyleData>;

export interface IEdgeStyleData extends VALID_EDGE_STYLE_MEDIA_MAP {
  bottom?: string;
  horizontal?: string;
  left?: string;
  right?: string;
  top?: string;
  vertical?: string;
}

export type VALID_EDGE_STYLE_DATA = string | IEdgeStyleData | undefined;
export const VALID_EDGE_STYLE_KINDS = ['padding', 'margin', 'row-gap', 'column-gap'];

// tslint:disable-next-line: cyclomatic-complexity
export const calcEdgeStyles = curry((
  theme: IThemeInterface,
  kind: typeof VALID_EDGE_STYLE_KINDS[number],
  data: VALID_EDGE_STYLE_DATA,
) => {
  if (data === undefined) { return undefined; }

  if (typeof data === 'string') {
    return css`
      ${kind}: ${theme.global.edgeSize[data] || data};
    `;
  }

  const styles: FlattenSimpleInterpolation[] = [];
  const pickedMedia: VALID_EDGE_STYLE_MEDIA_MAP = makeMediaObj(data);

  if (!isEmpty(pickedMedia)) {
    const pickedStringMedia = pickBy(pickedMedia, isString);
    const normalizedStringMedia: VALID_EDGE_STYLE_MEDIA_MAP = normalizeMediaStyles(theme, pickedStringMedia, 'edgeSize');
    const pickedObjectMedia = pickBy(pickedMedia, isObject);
    const normalizedObjectMedia = mapValues(pickedObjectMedia, (value, _) => normalizeMediaStyles(theme, value, 'edgeSize'));
    const normalizedMedia = merge(normalizedStringMedia, normalizedObjectMedia);

    styles.push(css`
      ${calcMediaStyles(normalizedMedia, kind)};
    `);
  }

  const {
    bottom,
    horizontal,
    left,
    right,
    top,
    vertical,
  } = data;

  if (horizontal) {
    styles.push(css`
      ${kind}-left: ${theme.global.edgeSize[horizontal] || horizontal};
      ${kind}-right: ${theme.global.edgeSize[horizontal] || horizontal};
    `);
  }

  if (vertical) {
    styles.push(css`
      ${kind}-top: ${theme.global.edgeSize[vertical] || vertical};
      ${kind}-bottom: ${theme.global.edgeSize[vertical] || vertical};
    `);
  }

  if (top) {
    styles.push(css`
      ${kind}-top: ${theme.global.edgeSize[top] || top};
    `);
  }

  if (right) {
    styles.push(css`
      ${kind}-right: ${theme.global.edgeSize[right] || right};
    `);
  }

  if (bottom) {
    styles.push(css`
      ${kind}-bottom: ${theme.global.edgeSize[bottom] || bottom};
    `);
  }

  if (left) {
    styles.push(css`
      ${kind}-left: ${theme.global.edgeSize[left] || left};
    `);
  }

  return styles;
});
